import { defineStore } from "pinia"
import { startOfYear, endOfYear, getYear } from "date-fns"
import { fetchApiJson, postApiJson } from "@/services/repowerAPIService.js"
import useUserStore from "@/stores/userStore.js"

export default defineStore("invoicesStore", {
  state: () => ({
    dateRange: [startOfYear(new Date()), endOfYear(new Date())],
    invoices: [],
    loading: false,
    loadingFailed: false,
    invoiceEmail: "",
    iban: "",
    streetNameAndNumber: "",
    buildingApartment: "",
    postalCode: "",
    city: "",
    country: "",
    isInvoiceSettingsLoaded: false,
  }),
  actions: {
    setInvoiceEmail(email) {
      this.invoiceEmail = email ?? ""
    },
    setIban(iban) {
      this.iban = iban ?? ""
    },
    setInvoiceAddress(
      streetNameAndNumber,
      buildingApartment,
      postalCode,
      city,
      country,
    ) {
      this.streetNameAndNumber = streetNameAndNumber ?? ""
      this.buildingApartment = buildingApartment ?? ""
      this.postalCode = postalCode ?? ""
      this.city = city ?? ""
      this.country = country ?? ""
    },
    resetInvoiceSettings() {
      this.setInvoiceEmail()
      this.setIban()
      this.setInvoiceAddress()
    },
    // TODO(TOF-289): exact-invoices (Exact Online) is now the default; users with
    // the "yoobi-download" flag still use the old Yoobi invoices/ endpoint. The
    // backend keeps both routes available for a transition period; remove the
    // flag check and the backend's Yoobi routes once this has fully rolled out.
    loadInvoices(portfolioId, onSuccess, onFail) {
      this.loading = true
      this.loadingFailed = false
      const endpoint = useUserStore().hasFlag("yoobi-download")
        ? "invoices"
        : "exact-invoices"
      return fetchApiJson(
        `/portfolios/${portfolioId}/${endpoint}/?year=${getYear(this.dateRange[0])}`,
      )
        .then((data) => {
          this.invoices = data
          onSuccess?.(data)
        })
        .catch((error) => {
          this.invoices = []
          this.loadingFailed = true
          onFail?.(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getInvoicePdf(portfolioId, invoiceNumber, invoiceId, onSuccess, onFail) {
      const useYoobiDownload = useUserStore().hasFlag("yoobi-download")
      const url = useYoobiDownload
        ? `/portfolios/${portfolioId}/invoices/${invoiceNumber}/pdf/`
        : `/portfolios/${portfolioId}/exact-invoices/${invoiceId}/pdf/`
      return fetchApiJson(url)
        .then((response) => {
          onSuccess?.(useYoobiDownload ? response.data : response.file)
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    loadInvoiceSettings(portfolioId, onSuccess, onFail) {
      this.isInvoiceSettingsLoaded = false
      return fetchApiJson(`/portfolios/${portfolioId}/invoice-details/`)
        .then(({ data }) => {
          this.setInvoiceEmail(data.email)
          this.setIban(data.iban)
          this.setInvoiceAddress(
            data.address.street_number_addition,
            data.address.building_apartment,
            data.address.postal_code,
            data.address.city,
            data.address.country,
          )
          onSuccess?.(data)
        })
        .catch((error) => {
          this.resetInvoiceSettings()
          onFail?.(error)
        })
        .finally(() => {
          this.isInvoiceSettingsLoaded = true
        })
    },
    requestInvoiceEmailChange(portfolioId, newEmail, onSuccess, onFail) {
      return postApiJson(
        `/portfolios/${portfolioId}/invoice-details/request-email-change/`,
        { invoice_email: newEmail },
        { method: "POST" },
      )
        .then((data) => {
          onSuccess?.(data)
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeInvoiceEmail(newEmail, token, password, onSuccess, onFail) {
      postApiJson(
        `/portfolios/invoice-details/change-email/`,
        { token, password },
        { method: "PUT" },
      )
        .then(() => {
          this.invoiceEmail = newEmail
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeIban(portfolioId, newIban, onSuccess, onFail) {
      postApiJson(
        `/portfolios/${portfolioId}/invoice-details/change-iban/`,
        { invoice_iban: newIban },
        { method: "PUT" },
      )
        .then(() => {
          this.iban = newIban
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeInvoiceAddress(portfolioId, address, onSuccess, onFail) {
      postApiJson(
        `/portfolios/${portfolioId}/invoice-details/change-address/`,
        {
          street_number_addition: address.streetNameAndNumber,
          building_apartment: address.buildingApartment,
          postal_code: address.postalCode,
          city: address.city,
          country: address.country,
        },
        { method: "PUT" },
      )
        .then(() => {
          this.setInvoiceAddress(
            address.streetNameAndNumber,
            address.buildingApartment,
            address.postalCode,
            address.city,
            address.country,
          )
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
  },
})
